// The payload's ordered column descriptors ({id, title_key XOR title_text},
// one entry per visible column, in render order) - the single source of
// truth for column visibility, order, and titles. The trailing round_to_par
// and round_thru entries are not columns: they are the league's overwrites
// for the client-built in-progress round headers (To Par and Thru), so they
// are extracted separately and never reach the column list. round_thru is
// consumed ahead of its backend producer landing - absent means the client
// keeps its localized default, so payloads without it behave as today.
const normalizeColumns = (apiColumns) => {
  const columns = [];
  let roundToParTitle = null;
  let roundThruTitle = null;

  (Array.isArray(apiColumns) ? apiColumns : []).forEach((column) => {
    if (!column?.id) return;

    if (column.id === 'round_to_par') {
      roundToParTitle = column.title_text ?? null;
      return;
    }

    if (column.id === 'round_thru') {
      roundThruTitle = column.title_text ?? null;
      return;
    }

    columns.push({
      id: column.id,
      titleKey: column.title_key ?? null,
      titleText: column.title_text ?? null,
    });
  });

  return { columns, roundToParTitle, roundThruTitle };
};

export const normalizeResultScope = (apiEvent) => {
  if (!apiEvent || !apiEvent.id) return {};

  const eventId = String(apiEvent.id_str);
  const name = apiEvent.name;

  const rounds = apiEvent.rounds
    ? Object.fromEntries(apiEvent.rounds.map(({ id, in_progress, course_abbreviations_display, ...rest }) => [
        id,
        { ...rest, inProgress: in_progress, courseAbbreviationsDisplay: course_abbreviations_display },
      ]))
    : {};

  const displayCut = !!apiEvent.display_cut;
  const { columns, roundToParTitle, roundThruTitle } = normalizeColumns(apiEvent.columns);
  let cutListPosition = {};
  let cutListLabel = {};
  let resolveTiesButton = {};
  let sectionLines = {};

  const scopesArray = Array.isArray(apiEvent.scopes) ? apiEvent.scopes : [];

  const eventNode = {};

  const sortedRoundIds = (apiEvent?.rounds ?? [])
    .sort((a, b) => a.index - b.index)
    .map(r => r.id);

  for (const scope of scopesArray) {
    const scopeId = String(scope.id_str);

    const aggregates = Array.isArray(scope.aggregates) ? scope.aggregates : [];

    const scopeNode = [];

    cutListPosition[scopeId] = scope.cut_list_position || '';
    cutListLabel[scopeId] = scope.cut_line_label ?? null;
    resolveTiesButton[scopeId] = scope.resolve_ties_button ?? null;

    const sectionLinesByPosition = (scope.section_lines ?? []).reduce((acc, line) => {
      acc[line.position] = line.label;
      return acc;
    }, {});
    sectionLines[scopeId] = sectionLinesByPosition;

    for (const agg of aggregates) {
      const aggregateId = String(agg.id_str);

      const memberIds =
        Array.isArray(agg.member_ids_str) && agg.member_ids_str.length
          ? agg.member_ids_str
          : (Array.isArray(agg.member_ids) ? agg.member_ids.map(String) : []);

      let currentRound = '';
      let pastRounds = [];

      if (agg.rounds !== undefined) {
        currentRound = agg.rounds[0] || '';
      }

      if (sortedRoundIds.length !== 0) {
        const roundsById = Object.fromEntries(agg.rounds.map(round => [round.id, round]));
        sortedRoundIds.forEach((roundId) => pastRounds.push(roundsById[roundId]));
      }

      pastRounds = pastRounds.map((round) => {
        if (!round) return round;
        const { course_abbreviations_display, ...rest } = round;
        return { ...rest, courseAbbreviationsDisplay: course_abbreviations_display };
      });

      const memberNames = (agg.member_names || []).map(({ first_name, last_name, ...rest }) => ({
        ...rest,
        firstName: first_name,
        lastName: last_name,
      }));

      scopeNode.push({
        id: aggregateId,
        memberIds,
        name: agg.name ?? '',
        memberNames,
        affiliation: agg.affiliation ?? '',
        position: agg.position ?? '',
        // The cause is shown as an info tooltip on the position and total
        // cells for dispositioned rows (DQ/WD/...), like the legacy
        // leaderboard, and on the round that introduced the disposition -
        // identified as the round entry whose total renders the disposition
        // string itself ("DQ") rather than a score.
        disposition: agg.disposition ?? '',
        dispositionCause: agg.disposition_cause ?? '',
        score: agg.score ?? '',
        thru: agg.thru ?? '',
        pastRounds,
        currentRound,
        total: agg.total ?? '',
        purse: agg.purse ?? '',
        points: agg.points ?? '',
        details: agg.details ?? '',
        ...('pos_change' in agg && { posChange: agg.pos_change }),
        country: agg.country ?? [],
        // State codes ('CA'), resolved to images through the initial state's
        // stateFlagUrls map (asset URLs are digested, so they cannot be built
        // client-side and must not be baked into the cached results blob).
        stateIsos: agg.state_isos ?? [],
        customLogos: agg.custom_logos ?? [],
        courseAbbreviationsDisplay: agg.course_abbreviations_display ?? null,
        netScores: agg.net_scores ?? [],
        grossScores: agg.gross_scores ?? [],
        toParNet: agg.to_par_net ?? [],
        // X markers per hole (-2/true = X, -101..-109 = X1..X9), the same
        // encoding the details endpoint ships per net. The blob substitutes nil
        // for X-ed holes in net_scores, so this is the only way to tell an X
        // from an unplayed hole; absent on blobs cached before the key existed.
        xesByHole: agg.xes_by_hole ?? [],
        // Running Out / In / Tot for the scorecard (NewAggregate#stroke_sum_front
        // etc., as aggregates_details already ships); undefined on blobs cached
        // before the fields existed, so the reader can fall back.
        strokeSums: {
          front: agg.stroke_sum_front ?? undefined,
          back: agg.stroke_sum_back ?? undefined,
          total: agg.stroke_sum ?? undefined,
        },
        toParGross: agg.to_par_gross ?? [],
        strikethroughsStrokesByHole: agg.strikethroughs_strokes_by_hole ?? [],
        totals: agg.totals
          ? { ...agg.totals, netScores: agg.totals.net_scores, grossScores: agg.totals.gross_scores }
          : {},
      });
    }
    eventNode[scopeId] = scopeNode;
  }

  return ({
    [eventId]: {
      name,
      rounds,
      scopes: eventNode,
      displayCut,
      columns,
      roundToParTitle,
      roundThruTitle,
      cutListPosition,
      cutListLabel,
      resolveTiesButton,
      sectionLines,
    }
  });
};
